/**
 * 세이브 관리. 캐릭터 슬롯 여러 개를 한 파일에 둔다.
 *
 * 저장소는 `storage.ts` 가 고른다. 부팅 시 한 번 읽고, 저장은 캐시 후 비동기 큐.
 */

import { PLAYER, PROGRESSION } from '../data/balance';
import { DEFAULT_JOB_ID, getJob, isFirstJob } from '../data/jobs';
import { DEFAULT_TITLE, ROSTER } from '../data/titles';
import { STARTER_WEAPON_ID } from '../data/weapons';
import { PlayerState, SAVE_VERSION, type SaveData } from './PlayerState';
import { apForLoadedLevel } from './stats';
import { resolveStorage, type SaveStorage } from './storage';

let storage: SaveStorage | null = null;
let roster: Roster = emptyRoster();
let pending: Promise<void> = Promise.resolve();

export interface RosterSlot {
  id: string;
  save: SaveData;
}

export interface Roster {
  kind: 'roster';
  rosterVersion: 1;
  activeId: string | null;
  slots: RosterSlot[];
}

interface LegacySaveV1 {
  version: 1;
  level: number;
  exp: number;
  hp: number;
  mp: number;
  meso: number;
  inventory: SaveData['inventory'];
  mapId: string;
  spawnId: string;
  playtimeMs: number;
  savedAt: number;
}

function emptyRoster(): Roster {
  return { kind: 'roster', rosterVersion: 1, activeId: null, slots: [] };
}

function newSlotId(): string {
  return `c-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

function warriorSkillDefaults(): Pick<SaveData, 'jobId' | 'sp' | 'skillLevels' | 'quickSlots' | 'quests' | 'worn'> {
  const job = getJob(DEFAULT_JOB_ID);
  return {
    jobId: job.id,
    sp: PROGRESSION.startSp,
    skillLevels: { powerStrike: 1, slashBlast: 1 },
    quickSlots: [...job.quickSlots],
    quests: {},
    worn: {},
  };
}

function toCurrent(
  partial: Partial<SaveData> &
    Pick<
      SaveData,
      'level' | 'exp' | 'hp' | 'mp' | 'meso' | 'inventory' | 'mapId' | 'spawnId' | 'playtimeMs' | 'savedAt' | 'ap' | 'weaponId'
    >,
): SaveData {
  const extras = warriorSkillDefaults();
  const name =
    typeof partial.name === 'string' && partial.name.trim()
      ? partial.name.trim().slice(0, ROSTER.nameMax)
      : PLAYER.defaultName;
  const unlocked = Array.isArray(partial.unlockedTitles)
    ? [...new Set([DEFAULT_TITLE, ...partial.unlockedTitles.filter((id) => typeof id === 'string')])]
    : [DEFAULT_TITLE];
  const achievements = Array.isArray(partial.unlockedAchievements)
    ? partial.unlockedAchievements.filter((id) => typeof id === 'string')
    : [];
  const maxCombo = typeof partial.maxCombo === 'number' ? Math.max(0, Math.floor(partial.maxCombo)) : 0;
  const titleId =
    typeof partial.titleId === 'string' && unlocked.includes(partial.titleId) ? partial.titleId : DEFAULT_TITLE;
  const kills: Record<string, number> = {};
  if (partial.kills && typeof partial.kills === 'object') {
    for (const [id, n] of Object.entries(partial.kills)) {
      if (typeof n === 'number' && n > 0) kills[id] = Math.floor(n);
    }
  }
  return {
    version: SAVE_VERSION,
    name,
    titleId,
    unlockedTitles: unlocked,
    unlockedAchievements: achievements,
    maxCombo,
    kills,
    level: partial.level,
    exp: partial.exp,
    hp: partial.hp,
    mp: partial.mp,
    meso: partial.meso,
    inventory: partial.inventory,
    mapId: partial.mapId,
    spawnId: partial.spawnId,
    playtimeMs: partial.playtimeMs,
    savedAt: partial.savedAt,
    ap: partial.ap,
    weaponId: partial.weaponId,
    jobId: getJob(partial.jobId as string | undefined).id,
    sp: typeof partial.sp === 'number' ? partial.sp : extras.sp,
    skillLevels: partial.skillLevels ?? extras.skillLevels,
    quickSlots: partial.quickSlots ?? extras.quickSlots,
    quests: partial.quests ?? extras.quests,
    worn: partial.worn ?? extras.worn,
  };
}

function migrateCharacter(raw: unknown): SaveData | null {
  if (!raw || typeof raw !== 'object') return null;
  const data = raw as Record<string, unknown>;
  const version = data.version;
  if (version === 1) {
    const v1 = data as unknown as LegacySaveV1;
    return toCurrent({
      level: v1.level,
      exp: v1.exp,
      hp: v1.hp,
      mp: v1.mp,
      meso: v1.meso,
      inventory: v1.inventory,
      mapId: v1.mapId,
      spawnId: v1.spawnId,
      playtimeMs: v1.playtimeMs,
      savedAt: v1.savedAt,
      ap: apForLoadedLevel(v1.level),
      weaponId: STARTER_WEAPON_ID,
    });
  }
  if (typeof version === 'number' && version >= 2 && version <= SAVE_VERSION && typeof data.level === 'number') {
    return toCurrent({
      level: data.level,
      exp: typeof data.exp === 'number' ? data.exp : 0,
      hp: typeof data.hp === 'number' ? data.hp : 1,
      mp: typeof data.mp === 'number' ? data.mp : 0,
      meso: typeof data.meso === 'number' ? data.meso : 0,
      inventory: Array.isArray(data.inventory) ? (data.inventory as SaveData['inventory']) : [],
      mapId: typeof data.mapId === 'string' ? data.mapId : '',
      spawnId: typeof data.spawnId === 'string' ? data.spawnId : '',
      playtimeMs: typeof data.playtimeMs === 'number' ? data.playtimeMs : 0,
      savedAt: typeof data.savedAt === 'number' ? data.savedAt : Date.now(),
      ap: (data.ap as SaveData['ap'] | undefined) ?? apForLoadedLevel(data.level, data.jobId as string | undefined),
      weaponId: typeof data.weaponId === 'string' ? data.weaponId : STARTER_WEAPON_ID,
      jobId: typeof data.jobId === 'string' ? data.jobId : undefined,
      sp: typeof data.sp === 'number' ? data.sp : undefined,
      skillLevels: data.skillLevels as Record<string, number> | undefined,
      quickSlots: Array.isArray(data.quickSlots) ? (data.quickSlots as string[]) : undefined,
      quests: data.quests as SaveData['quests'] | undefined,
      worn: data.worn as SaveData['worn'] | undefined,
      name: typeof data.name === 'string' ? data.name : undefined,
      titleId: typeof data.titleId === 'string' ? data.titleId : undefined,
      unlockedTitles: Array.isArray(data.unlockedTitles) ? (data.unlockedTitles as string[]) : undefined,
      unlockedAchievements: Array.isArray(data.unlockedAchievements)
        ? (data.unlockedAchievements as string[])
        : undefined,
      maxCombo: typeof data.maxCombo === 'number' ? data.maxCombo : undefined,
      kills: data.kills as Record<string, number> | undefined,
    });
  }
  return null;
}

function wrapLegacy(save: SaveData): Roster {
  const id = newSlotId();
  return { kind: 'roster', rosterVersion: 1, activeId: id, slots: [{ id, save }] };
}

function parseRoster(raw: string | null): Roster {
  if (!raw) return emptyRoster();
  try {
    const data = JSON.parse(raw) as unknown;
    if (!data || typeof data !== 'object') return emptyRoster();
    const rec = data as Record<string, unknown>;
    if (rec.kind === 'roster' && Array.isArray(rec.slots)) {
      const slots: RosterSlot[] = [];
      for (const row of rec.slots) {
        if (!row || typeof row !== 'object') continue;
        const slot = row as Record<string, unknown>;
        const save = migrateCharacter(slot.save);
        if (!save) continue;
        const id = typeof slot.id === 'string' ? slot.id : newSlotId();
        slots.push({ id, save });
      }
      const activeId = typeof rec.activeId === 'string' && slots.some((s) => s.id === rec.activeId)
        ? rec.activeId
        : (slots[0]?.id ?? null);
      return { kind: 'roster', rosterVersion: 1, activeId, slots };
    }
    const one = migrateCharacter(data);
    return one ? wrapLegacy(one) : emptyRoster();
  } catch {
    return emptyRoster();
  }
}

function persist(): void {
  const target = storage;
  if (!target) return;
  const text = JSON.stringify(roster);
  pending = pending
    .then(() => target.write(text))
    .catch((error) => console.warn('[save] 저장 실패', error));
}

function activeSlot(): RosterSlot | undefined {
  return roster.slots.find((s) => s.id === roster.activeId) ?? roster.slots[0];
}

export const SaveGame = {
  async init(): Promise<SaveData | null> {
    storage = await resolveStorage();
    try {
      roster = parseRoster(await storage.read());
    } catch (error) {
      console.warn('[save] 세이브를 읽지 못했습니다', error);
      roster = emptyRoster();
    }
    return this.cached();
  },

  cached(): SaveData | null {
    return activeSlot()?.save ?? null;
  },

  slots(): RosterSlot[] {
    return roster.slots;
  },

  activeId(): string | null {
    return roster.activeId;
  },

  exists(): boolean {
    return roster.slots.length > 0;
  },

  select(id: string): boolean {
    if (!roster.slots.some((s) => s.id === id)) return false;
    roster.activeId = id;
    persist();
    return true;
  },

  save(data: SaveData): void {
    const slot = activeSlot();
    if (!slot) {
      const id = newSlotId();
      roster.slots.push({ id, save: data });
      roster.activeId = id;
    } else {
      slot.save = data;
    }
    persist();
  },

  create(name: string, jobId: string): string | null {
    const trimmed = name.trim();
    if (trimmed.length < ROSTER.nameMin || trimmed.length > ROSTER.nameMax) {
      return `이름은 ${ROSTER.nameMin}~${ROSTER.nameMax}자`;
    }
    if (!isFirstJob(jobId)) return '1차 직업만 만들 수 있습니다';
    if (trimmed.includes('/')) return '이름에 / 를 쓸 수 없습니다';
    if (roster.slots.length >= ROSTER.maxSlots) return `슬롯은 ${ROSTER.maxSlots}개까지`;
    if (roster.slots.some((s) => s.save.name === trimmed)) return '이미 있는 이름입니다';
    const fresh = new PlayerState();
    fresh.beginJob(jobId, trimmed);
    const save = fresh.toJSON();
    const id = newSlotId();
    roster.slots.push({ id, save });
    roster.activeId = id;
    persist();
    return null;
  },

  remove(id: string): void {
    roster.slots = roster.slots.filter((s) => s.id !== id);
    if (roster.activeId === id) roster.activeId = roster.slots[0]?.id ?? null;
    persist();
  },

  async flush(): Promise<void> {
    await pending;
  },

  clear(): void {
    roster = emptyRoster();
    const target = storage;
    if (!target) return;
    pending = pending
      .then(() => target.remove())
      .catch((error) => console.warn('[save] 삭제 실패', error));
  },

  get location(): string {
    return storage?.location ?? '(초기화 전)';
  },

  get backend(): string {
    return storage?.kind ?? 'unknown';
  },
};
